import React, { useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { candidateService } from '../services/candidateService';
import Layout from '../components/Layout';
import PasswordChangeModal from '../components/PasswordChangeModal';
import CandidateDrawer from '../components/CandidateDrawer';

export default function CandidateGraph() {
  const [user, setUser] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scoringConfig, setScoringConfig] = useState(null);

  // Filter state
  const [filters, setFilters] = useState({
    sector: '',
    roleFamily: '',
    geography: '',
    lastTouch: ''
  });

  const [uniqueSectors, setUniqueSectors] = useState([]);
  const [uniqueRoles, setUniqueRoles] = useState([]);
  const [uniqueGeographies, setUniqueGeographies] = useState([]);
  
  const lastTouchOptions = ['Last 7 days', 'Last 30 days', 'Last 90 days'];

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const changePasswordParam = urlParams.get('changePassword');
    
    if (changePasswordParam === 'true') {
      const userStr = sessionStorage.getItem('signalcue_user');
      if (userStr) {
        const userData = JSON.parse(userStr);
        setUser(userData);
        if (userData.must_change_password) {
          setShowPasswordModal(true);
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          loadUserData();
        }
      } else {
        loadUserData();
      }
    } else {
      loadUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user && !user.must_change_password) {
      loadFilterOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user && !user.must_change_password) {
      loadCandidates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, user]);

  const extractFilterOptions = (candidateRows) => {
    const sectors = new Set();
    const roles = new Set();
    const geos = new Set();

    candidateRows.forEach((candidate) => {
      if (candidate.sector) sectors.add(candidate.sector);
      if (candidate.role_family) roles.add(candidate.role_family);
      if (candidate.city || candidate.country) {
        const location = [candidate.city, candidate.country].filter(Boolean).join(', ');
        if (location) geos.add(location);
      }
    });

    return {
      sectors: Array.from(sectors).sort(),
      roles: Array.from(roles).sort(),
      geographies: Array.from(geos).sort()
    };
  };

  const loadUserData = async () => {
    try {
      const userData = await authService.getCurrentUser();
      if (!userData) {
        const loginUrl = process.env.REACT_APP_LOGIN_URL || (window.location.hostname.includes('-staging') ? window.location.origin : window.location.origin.replace('app.signalcue.ai', 'signalcue.ai'));
        window.location.href = `${loginUrl}/login`;
        return;
      }
      
      setUser(userData);
      
      if (userData.must_change_password) {
        setShowPasswordModal(true);
      }
    } catch (error) {
      console.error('Failed to load user:', error);
      const loginUrl = process.env.REACT_APP_LOGIN_URL || (window.location.hostname.includes('-staging') ? window.location.origin : window.location.origin.replace('app.signalcue.ai', 'signalcue.ai'));
      window.location.href = `${loginUrl}/login`;
    }
  };

  const loadCandidates = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Build filter params
      const filterParams = {};
      if (filters.sector) filterParams.sector = filters.sector;
      if (filters.roleFamily) filterParams.role_family = filters.roleFamily;
      if (filters.geography) {
        const [city, country] = filters.geography
          .split(',')
          .map((part) => part.trim())
          .filter(Boolean);

        // The API matches city/country independently. Dropdown values are stored
        // as "City, Country", so split them before sending the request.
        if (city) filterParams.city = city;
        if (country) filterParams.country = country;
        if (!city && !country) filterParams.geography = filters.geography;
      }
      if (filters.lastTouch) {
        const days = filters.lastTouch === 'Last 7 days' ? 7 : filters.lastTouch === 'Last 30 days' ? 30 : 90;
        filterParams.last_touch_days = days;
      }
      
      // Note: API expects role_family, not roleFamily
      
      const data = await candidateService.getCandidateGraph(200, 0, filterParams);
      if (data.scoring_config) {
        setScoringConfig(data.scoring_config);
      }

      // Sort by fit score (highest to lowest) as per UI spec
      const sortedCandidates = [...data.candidates].sort((a, b) => {
        const aScore = a.fit_score || 0;
        const bScore = b.fit_score || 0;
        return bScore - aScore;
      });
      
      setCandidates(sortedCandidates);
    } catch (err) {
      setError(err.message);
      console.error('Failed to load candidates:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const data = await candidateService.getCandidateGraph(200, 0, {});
      const options = extractFilterOptions(data.candidates);
      setUniqueSectors(options.sectors);
      setUniqueRoles(options.roles);
      setUniqueGeographies(options.geographies);
    } catch (err) {
      console.error('Failed to load candidate filter options:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleRowClick = async (candidate) => {
    try {
      // Fetch full candidate details for drawer
      const candidateDetails = await candidateService.getCandidateProfile(candidate.candidate_id);
      setSelectedCandidate(candidateDetails);
      setDrawerOpen(true);
    } catch (err) {
      console.error('Failed to load candidate details:', err);
      // Fallback to basic candidate data
      setSelectedCandidate(candidate);
      setDrawerOpen(true);
    }
  };

  const getFitScoreTextColor = (score) => {
    const callNow = scoringConfig?.buckets?.call_now ?? 80;
    const warm = scoringConfig?.buckets?.warm ?? 60;
    if (score >= callNow) return 'text-emerald-700';
    if (score >= warm) return 'text-amber-700';
    return 'text-gray-700';
  };

  const getFitScoreBgColor = (score) => {
    const callNow = scoringConfig?.buckets?.call_now ?? 80;
    const warm = scoringConfig?.buckets?.warm ?? 60;
    if (score >= callNow) return 'bg-emerald-100';
    if (score >= warm) return 'bg-amber-100';
    return 'bg-gray-100';
  };

  const getProvenanceBadges = (candidate) => {
    const badges = [];
    // Prefer API data_sources when present
    if (candidate.data_sources?.length > 0) {
      candidate.data_sources.forEach((src) => {
        const icon = src.type === 'ats' ? 'database' : src.type === 'spreadsheet' ? 'spreadsheet' : 'globe';
        badges.push({ type: src.type, icon });
      });
      return badges;
    }
    // Fallback: infer from confidence (0-1 or 0-100) and signals
    if (candidate.confidence != null && (candidate.confidence > 0.8 || (candidate.confidence > 1 && candidate.confidence >= 80))) {
      badges.push({ type: 'ats', icon: 'database' });
    }
    if (candidate.explanatory_factors?.signals_used?.length > 0) {
      badges.push({ type: 'linkedin', icon: 'globe' });
    }
    return badges;
  };

  if (loading && !showPasswordModal && candidates.length === 0 && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading candidates...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <Layout user={user}>
      {/* Password Change Modal */}
      {showPasswordModal && (
        <PasswordChangeModal
          user={user}
          onPasswordChanged={handlePasswordChanged}
          onClose={() => setShowPasswordModal(false)}
        />
      )}

      {/* Filter Rail */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Sector
            </label>
            <select
              value={filters.sector}
              onChange={(e) => handleFilterChange('sector', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B3A6B] focus:border-[#1B3A6B] text-sm"
            >
              <option value="">All Sectors</option>
              {uniqueSectors.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Role Family
            </label>
            <select
              value={filters.roleFamily}
              onChange={(e) => handleFilterChange('roleFamily', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B3A6B] focus:border-[#1B3A6B] text-sm"
            >
              <option value="">All Role Families</option>
              {uniqueRoles.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Geography
            </label>
            <select
              value={filters.geography}
              onChange={(e) => handleFilterChange('geography', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B3A6B] focus:border-[#1B3A6B] text-sm"
            >
              <option value="">All Locations</option>
              {uniqueGeographies.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Last Touch Window
            </label>
            <select
              value={filters.lastTouch}
              onChange={(e) => handleFilterChange('lastTouch', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B3A6B] focus:border-[#1B3A6B] text-sm"
            >
              <option value="">All Time</option>
              {lastTouchOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Candidates Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-hidden">
          <table className="w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-[22%] px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Candidate Name &darr;
                </th>
                <th className="w-[22%] px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Employer &amp; Title
                </th>
                <th className="w-[26%] px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Placement History
                </th>
                <th className="w-[10%] px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fit Score
                </th>
                <th className="w-[12%] px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Provenance
                </th>
                <th className="w-[8%] px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {candidates.map((candidate) => {
                const fitScore = candidate.fit_score || 0;
                const breakdown = candidate.fit_score_breakdown || {};
                
                return (
                  <tr
                    key={candidate.candidate_id}
                    onClick={() => handleRowClick(candidate)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    {/* Candidate Name */}
                    <td className="px-3 py-3 align-top">
                      <div className="text-sm font-medium text-gray-900 truncate" title={candidate.full_name}>
                        {candidate.full_name}
                      </div>
                      {candidate.local_name && (
                        <div className="text-xs text-gray-500 mt-0.5 truncate" title={candidate.local_name}>
                          {candidate.local_name}
                        </div>
                      )}
                    </td>
                    
                    {/* Current Employer & Title */}
                    <td className="px-3 py-3 align-top">
                      <div className="text-sm font-semibold text-gray-900 truncate" title={candidate.current_company_name || '-'}>
                        {candidate.current_company_name || '-'}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 truncate" title={candidate.current_title || 'Unknown Title'}>
                        {candidate.current_title || 'Unknown Title'}
                      </div>
                    </td>

                    {/* Placement History */}
                    <td className="px-3 py-3 align-top">
                      {candidate.placement_history?.length > 0 ? (
                        <div className="text-sm min-w-0">
                          <span className="text-gray-900 block truncate" title={`${candidate.placement_history[0].from_company ? `${candidate.placement_history[0].from_company} → ` : ''}${candidate.placement_history[0].to_company}`}>
                            {candidate.placement_history[0].from_company ? `${candidate.placement_history[0].from_company} → ` : ''}
                            {candidate.placement_history[0].to_company}
                          </span>
                          {candidate.placement_history[0].hiring_manager && (
                            <div className="text-gray-500 text-xs mt-1 truncate" title={`HM: ${candidate.placement_history[0].hiring_manager.name} (${candidate.placement_history[0].hiring_manager.title})`}>
                              HM: {candidate.placement_history[0].hiring_manager.name} ({candidate.placement_history[0].hiring_manager.title})
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    
                    {/* Fit Score */}
                    <td className="px-3 py-3 align-top">
                      <div className="flex justify-center">
                        <div className={`w-10 h-10 rounded-xl ${getFitScoreBgColor(fitScore)} flex items-center justify-center`}>
                          <span className={`text-sm font-bold ${getFitScoreTextColor(fitScore)}`}>
                            {fitScore}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* Provenance */}
                    <td className="px-3 py-3 align-top">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {getProvenanceBadges(candidate).map((badge, idx) => (
                          <div key={idx} className="flex items-center space-x-1 px-2 py-0.5 bg-gray-50 border border-gray-200 rounded-full text-[11px] text-gray-600">
                            {badge.icon === 'database' && (
                              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"></path></svg>
                            )}
                            {badge.icon === 'globe' && (
                              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"></path></svg>
                            )}
                            <span className="capitalize">{badge.type === 'ats' ? 'ATS' : badge.type}</span>
                          </div>
                        ))}
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-3 py-3 align-top">
                      <div className="flex items-center justify-center gap-2 text-gray-400">
                        <svg className="w-4 h-4 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <svg className="w-4 h-4 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <svg className="w-4 h-4 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>


          {candidates.length === 0 && !loading && (
            <div className="text-center py-12 text-gray-500">
              No candidates found. Try adjusting your filters.
            </div>
          )}
        </div>
      </div>

      {/* Candidate Drawer */}
      <CandidateDrawer
        candidate={selectedCandidate}
        isOpen={drawerOpen}
        scoringConfig={scoringConfig}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedCandidate(null);
        }}
      />
    </Layout>
  );

  async function handlePasswordChanged() {
    const updatedUserData = await authService.getCurrentUser();
    if (updatedUserData) {
      setUser(updatedUserData);
    }
    await loadCandidates();
  }
}
